import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/api';
import { Alert, Button, Input, Label, Spinner } from '../components/ui';
import { cn } from '../lib/cn';

function MicrosoftIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 21 21" className="h-[18px] w-[18px]" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export function LoginPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { token, login, startSso, ssoError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (token) return <Navigate to="/dashboard" replace />;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans">
      {/* Foto-Hintergrund + Scrim für Lesbarkeit (≥4.5:1). */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/hero-containers.webp')" }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/55 to-slate-900/70" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" aria-hidden />

      {/* Logo oben im Himmel (transparent, ohne Chip) */}
      <div className="absolute left-6 top-6 z-20 sm:left-10 sm:top-8">
        <img
          src="/brand-fclai-transparent.webp"
          alt="fclAI"
          className="h-20 w-auto drop-shadow-[0_3px_16px_rgba(0,0,0,0.5)] sm:h-24 lg:h-28"
        />
      </div>

      {/* Sprachumschalter */}
      <div className="absolute right-5 top-5 z-20 flex items-center gap-1 rounded-full border border-white/15 bg-white/10 px-1 py-1 backdrop-blur-md">
        {(['de', 'en'] as const).map((lng) => (
          <button
            key={lng}
            onClick={() => void i18n.changeLanguage(lng)}
            className={cn(
              'cursor-pointer rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors',
              i18n.language === lng ? 'bg-white text-slate-900' : 'text-white/80 hover:text-white',
            )}
          >
            {lng.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="container relative z-10 grid min-h-screen items-center gap-12 py-12 lg:grid-cols-2">
        {/* Hero */}
        <div className="max-w-xl space-y-6 text-white">
          <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight drop-shadow-sm sm:text-5xl">
            {t('hero.headline')}
          </h1>
          <p className="max-w-md text-lg leading-relaxed text-white/85">{t('hero.subline')}</p>
        </div>

        {/* Apple-Glass-Login-Karte */}
        <div className="flex justify-center lg:justify-end">
          <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-white/40 bg-white/80 p-6 shadow-2xl backdrop-blur-2xl duration-500 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none dark:border-white/10 dark:bg-slate-900/65 sm:p-7">
            {/* Glas-Glanzkante oben (Apple-Look) */}
            <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent" aria-hidden />

            <div className="mb-5">
              <h2 className="text-lg font-semibold text-foreground">{t('auth.signInTitle')}</h2>
              <p className="text-sm text-muted-foreground">{t('auth.signInHint')}</p>
            </div>

            <Button
              variant="secondary"
              className="w-full border-transparent bg-white text-slate-800 shadow-sm hover:bg-white/90"
              onClick={startSso}
            >
              <MicrosoftIcon />
              {t('auth.ssoButton')}
            </Button>

            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {t('auth.or')}
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {(error || ssoError) && <Alert kind="error">{error || ssoError}</Alert>}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? <Spinner /> : t('auth.login')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
